import { Clients } from "../../types/enums";
import { LoanTypeForFields } from "../enums";
import { BRIDGE_V2_ID } from "./versions";

// Shared field lists reused across multiple loan types
const PROPERTY_LOCATION_FIELDS = ["address", "postalCode", "city", "state"];

const FIXFLIP_BORROWER_FIELDS = [
  "borrowerType",
  "propertiesCompletedWithinLast36Months",
  "BorrowerGuarantor",
  "ImmigrationStatus",
  "borrowingEntityName",
  "borrowerName",
  "borrowerEmail",
  "guarantorName",
  "guarantorEmail",
  "originalCreditScore",
  "creditReportDate",
  "borrowerAddress",
];

const GRUP_BORROWER_SFR_MF_FIELDS = [
  "borrowerType",
  "borrowerGUCExperience",
  "heavyRehabExperience",
  "originalCreditScore",
  "creditReportDate",
  "borrowingEntityName",
  "borrowerName",
  "borrowerEmail",
  "guarantorName",
  "guarantorEmail",
  "BorrowerGuarantor",
  "ImmigrationStatus",
];

const GRUP_BORROWER_UNIT24_FIELDS = [
  "borrowerType",
  "borrowerGUCExperience",
  "heavyRehabExperience",
  "BorrowerGuarantor",
  "ImmigrationStatus",
  "originalCreditScore",
  "creditReportDate",
  "borrowingEntityName",
  "borrowerName",
  "borrowerEmail",
  "guarantorName",
  "guarantorEmail",
];

const FIXFLIP_LOAN_INFO_FIELDS = [
  "OriginatorLoanID",
  "LoanStructure",
  "FixFlipMFLoanPurpose",
  "CashOutFlag",
  "recourse",
  "ExitStrategy",
  "LoanTerm",
];

const GRUP_LOAN_INFO_FIELDS = [
  "OriginatorLoanID",
  "LoanStructurev2",
  "CashOutFlag",
  "GRExitStrategy",
  "LoanTerm",
  "irFundingSource",
  "permitApprovalsAndFullPlans",
];

const FIXFLIP_LOAN_ECON_FIELDS = [
  // "exitDebt", // change as per TA-11656
  "hardCosts",
  "softCostsCap15Rehab",
  "InitialLoanAmount",
  "TotalOrgAndDiscountPoints",
  "interestRate",
  "financedBudget",
  "includeOutOfPocketBudgetARLTV",
  "InterestReserve",
];

const GRUP_LOAN_ECON_FIELDS = [
  "hardCosts",
  "softCosts",
  "InitialLoanAmount",
  "financedBudget",
  "InterestReserve",
  "orgBrokerFee",
  "interestRate",
  "totalBudget",
];

const FIXFLIP_PROPERTY_INFO_FIELDS = [
  "propertyType",
  "ExpansionOrChangeOfUseCase",
  "Expansion",
  "ValuationSource",
  "PercentOccupied",
];

const GRUP_PROPERTY_INFO_FIELDS = [
  "propertyType",
  "noOfUnits",
  "ValuationSource",
  "propertyPurchasedWithinLast2Years",
  "entitledAsOfRightDevelopment",
  "isPurchasedWithDebt",
];

const FIXFLIP_PROPERTY_ECON_FIELDS = [
  "orgAppraisalValue",
  "purchasePrice",
  "borrowerPaidCostToDate",
  "AsRepairedValueARV",
  "EstimatedRentPerYearUponCompletion",
  "appraisalStabilizedNOI",
  "annualPropertyTaxes",
  "costBasis",
  // "grossCondoSelloutValue", // change as per TA-11656
  "AssignmentFee",
  "purchaseDate",
  "annualInsurance",
  "annualHOAFee",
];

const GRUP_PROPERTY_ECON_FIELDS = [
  "asIsValue",
  "EstimatedRentPerYearUponCompletion",
  "purchasePrice",
  "ClosingCost",
  "afterRepairValue",
  "AssignmentFee",
];

const FIXFLIP_LOAN_FEATURES = [
  "OriginalAsIsLtv",
  "originalAsRepairedLTV",
  "OriginalLtc",
];

const GRUP_LOAN_FEATURES = [
  "AsIsLTV",
  "LtcAsIs",
  "ARVLTVAsCompleted",
  "BlendedLTCAsCompleted",
  "BlendedLTCwithFundedIRAsCompleted",
];

const FIXFLIP_LOAN_CHAR_FIELDS = [
  "CustomerTypeExperiencedNew",
  "PurchaseOrRefinance",
  "rehabType",
  "StabilizedFlag",
  "loanCharacterization",
];

const GRUP_LOAN_CHAR_FIELDS = [
  "loanCharacterization",
  "Tier",
  "LoanPurpose",
];

const FIXFLIP_PROP_LOAN_FEATURES = [
  "cbsa",
  "MedianValue",
  "LtmHpa",
  "DaysOnMarket",
];

const GRUP_PROP_LOAN_FEATURES = [
  "cbsa",
  "MedianValue",
  "LtmHpa",
  "DaysOnMarket",
  "zipCodeDensity",
];

const MF_MARKET_DATA_FIELDS = [
  "MarketName",
  "CapRate",
  "TwelveMonthRentalGrowth",
  "RentPerSqFt",
  "RentPerUnit",
  "SalePriceGrowth",
  "SalePricePerUnit",
  "Population",
  "VacancyRate",
];

const MF_SUBMARKET_DATA_FIELDS = [
  "SubMarketName",
  "CapRate",
  "TwelveMonthRentalGrowth",
  "RentPerSqFt",
  "RentPerUnit",
  "SalePriceGrowth",
  "SalePricePerUnit",
  "VacancyRate",
];

const bridgeV2 = {
  id: BRIDGE_V2_ID,
  [Clients.BPL_SIZER]: {
    inputFields: {
      borrowerInformation: [
        "propertiesCompletedWithinLast36Months",
        "foreignNational",
        "ImmigrationStatus",
        "originalCreditScore",
        "borrowerAddress",
      ],
      loanInformation: [
        "OriginatorLoanID",
        "LoanStructure",
        "LoanPurpose",
        "CashOutFlag",
        "ExitStrategy",
        "pledgeOfEquity",
      ],
      loanEconomics: [
        "InitialLoanAmount",
        "OrgInterestRate",
        "TotalRehabBudget",
        "financedBudget",
        "TotalOrgAndDiscountPoints",
      ],
      propertyLocation: PROPERTY_LOCATION_FIELDS,
      propertyInformation: [
        "propertyType",
        "ExpansionOrChangeOfUseCase",
        "Expansion",
        "ValuationSource",
        "PercentOccupied",
      ],
      propertyEconomics: [
        "orgAppraisalValue",
        "purchasePrice",
        "ClosingCost",
        "AsRepairedValueARV",
        "EstimatedRentPerYearUponCompletion",
        "costBasis",
        "AssignmentFee",
        "annualPropertyTaxes",
        "annualInsurance",
        "annualHOAFee",
      ],
      loanFeatures: FIXFLIP_LOAN_FEATURES,
      loanCharacterization: FIXFLIP_LOAN_CHAR_FIELDS,
      propertyLoanFeatures: [
        "cbsa",
        "MedianValue",
        "LtmHpa",
        "DaysOnMarket",
        "ESRIPopulationDensity",
      ],
    },
    outputFields: {},
  },
  [Clients.MMTC]: {
    inputFields: {
      borrowerInformation: {
        quickLoanSizing: [],
        [LoanTypeForFields.FIXFLIP_MULTIFAMILYMIXEDUSE]: [
          "borrowerType",
          "propertiesCompletedWithinLast36Months",
          "borrowingEntityName",
          "borrowerName",
          "borrowerEmail",
          "guarantorName",
          "guarantorEmail",
          "originalCreditScore",
          "BorrowerGuarantor",
          "ImmigrationStatus",
          // "borrowerAddress",
        ],
        [LoanTypeForFields.FIXFLIP_SFR]: FIXFLIP_BORROWER_FIELDS,
        [LoanTypeForFields.FIXFLIP_UNIT_24]: FIXFLIP_BORROWER_FIELDS,
        [LoanTypeForFields.FIXFLIP_CONDO]: FIXFLIP_BORROWER_FIELDS,
        [LoanTypeForFields.FIXFLIP_TOWNHOME]: FIXFLIP_BORROWER_FIELDS,
        [LoanTypeForFields.GRUP_SFR]: GRUP_BORROWER_SFR_MF_FIELDS,
        [LoanTypeForFields.GRUP_UNIT_24]: GRUP_BORROWER_UNIT24_FIELDS,
        [LoanTypeForFields.GRUP_CONDO]: GRUP_BORROWER_UNIT24_FIELDS,
        [LoanTypeForFields.GRUP_TOWNHOME]: GRUP_BORROWER_UNIT24_FIELDS,
        [LoanTypeForFields.GRUP_MULTIFAMILYMIXEDUSE]: GRUP_BORROWER_SFR_MF_FIELDS,
      },
      loanInformation: {
        quickLoanSizing: [],
        [LoanTypeForFields.FIXFLIP_MULTIFAMILYMIXEDUSE]: [
          ...FIXFLIP_LOAN_INFO_FIELDS,
          "requiredInterestReserve",
          "requiredInterestReserveMonths",
        ],
        [LoanTypeForFields.FIXFLIP_SFR]: FIXFLIP_LOAN_INFO_FIELDS,
        [LoanTypeForFields.FIXFLIP_UNIT_24]: FIXFLIP_LOAN_INFO_FIELDS,
        [LoanTypeForFields.FIXFLIP_CONDO]: FIXFLIP_LOAN_INFO_FIELDS,
        [LoanTypeForFields.FIXFLIP_TOWNHOME]: FIXFLIP_LOAN_INFO_FIELDS,
        [LoanTypeForFields.GRUP_SFR]: GRUP_LOAN_INFO_FIELDS,
        [LoanTypeForFields.GRUP_UNIT_24]: GRUP_LOAN_INFO_FIELDS,
        [LoanTypeForFields.GRUP_CONDO]: GRUP_LOAN_INFO_FIELDS,
        [LoanTypeForFields.GRUP_TOWNHOME]: GRUP_LOAN_INFO_FIELDS,
        [LoanTypeForFields.GRUP_MULTIFAMILYMIXEDUSE]: [
          ...GRUP_LOAN_INFO_FIELDS,
          "requiredInterestReserve",
          "requiredInterestReserveMonths",
        ],
      },
      loanEconomics: {
        quickLoanSizing: [],
        [LoanTypeForFields.FIXFLIP_MULTIFAMILYMIXEDUSE]: [
          "exitDebt",
          "hardCosts",
          "softCostsCap15Rehab",
          "TotalRehabBudget",
          "InitialLoanAmount",
          "TotalOrgAndDiscountPoints",
          "interestRate",
          "financedBudget",
          "InterestReserve",
        ],
        [LoanTypeForFields.FIXFLIP_SFR]: FIXFLIP_LOAN_ECON_FIELDS,
        [LoanTypeForFields.FIXFLIP_UNIT_24]: FIXFLIP_LOAN_ECON_FIELDS,
        [LoanTypeForFields.FIXFLIP_CONDO]: FIXFLIP_LOAN_ECON_FIELDS,
        [LoanTypeForFields.FIXFLIP_TOWNHOME]: FIXFLIP_LOAN_ECON_FIELDS,
        [LoanTypeForFields.GRUP_SFR]: GRUP_LOAN_ECON_FIELDS,
        [LoanTypeForFields.GRUP_UNIT_24]: GRUP_LOAN_ECON_FIELDS,
        [LoanTypeForFields.GRUP_CONDO]: [...GRUP_LOAN_ECON_FIELDS, "permitApprovalsAndFullPlans"],
        [LoanTypeForFields.GRUP_TOWNHOME]: GRUP_LOAN_ECON_FIELDS,
        [LoanTypeForFields.GRUP_MULTIFAMILYMIXEDUSE]: GRUP_LOAN_ECON_FIELDS,
      },
      propertyLocation: {
        quickLoanSizing: [],
        [LoanTypeForFields.FIXFLIP_MULTIFAMILYMIXEDUSE]: PROPERTY_LOCATION_FIELDS,
        [LoanTypeForFields.FIXFLIP_SFR]: PROPERTY_LOCATION_FIELDS,
        [LoanTypeForFields.FIXFLIP_UNIT_24]: PROPERTY_LOCATION_FIELDS,
        [LoanTypeForFields.FIXFLIP_CONDO]: PROPERTY_LOCATION_FIELDS,
        [LoanTypeForFields.FIXFLIP_TOWNHOME]: PROPERTY_LOCATION_FIELDS,
        [LoanTypeForFields.GRUP_SFR]: PROPERTY_LOCATION_FIELDS,
        [LoanTypeForFields.GRUP_UNIT_24]: PROPERTY_LOCATION_FIELDS,
        [LoanTypeForFields.GRUP_CONDO]: [...PROPERTY_LOCATION_FIELDS, "permitApprovalsAndFullPlans"],
        [LoanTypeForFields.GRUP_TOWNHOME]: PROPERTY_LOCATION_FIELDS,
        [LoanTypeForFields.GRUP_MULTIFAMILYMIXEDUSE]: PROPERTY_LOCATION_FIELDS,
      },
      propertyInformation: {
        quickLoanSizing: [],
        [LoanTypeForFields.FIXFLIP_MULTIFAMILYMIXEDUSE]: [
          "mfPropertyType",
          "noOfUnits",
          "ExpansionOrChangeOfUseCase",
          "Expansion",
          "ValuationSource",
          "PercentOccupied",
        ],
        [LoanTypeForFields.FIXFLIP_SFR]: FIXFLIP_PROPERTY_INFO_FIELDS,
        [LoanTypeForFields.FIXFLIP_UNIT_24]: FIXFLIP_PROPERTY_INFO_FIELDS,
        [LoanTypeForFields.FIXFLIP_CONDO]: FIXFLIP_PROPERTY_INFO_FIELDS,
        [LoanTypeForFields.FIXFLIP_TOWNHOME]: FIXFLIP_PROPERTY_INFO_FIELDS,
        [LoanTypeForFields.GRUP_SFR]: GRUP_PROPERTY_INFO_FIELDS,
        [LoanTypeForFields.GRUP_UNIT_24]: GRUP_PROPERTY_INFO_FIELDS,
        [LoanTypeForFields.GRUP_CONDO]: GRUP_PROPERTY_INFO_FIELDS,
        [LoanTypeForFields.GRUP_TOWNHOME]: [...GRUP_PROPERTY_INFO_FIELDS, "state"],
        [LoanTypeForFields.GRUP_MULTIFAMILYMIXEDUSE]: [
          "mfPropertyType",
          "noOfUnits",
          "ValuationSource",
          "propertyPurchasedWithinLast2Years",
          "entitledAsOfRightDevelopment",
          "isPurchasedWithDebt",
        ],
      },
      propertyEconomics: {
        quickLoanSizing: [],
        [LoanTypeForFields.FIXFLIP_MULTIFAMILYMIXEDUSE]: FIXFLIP_PROPERTY_ECON_FIELDS,
        [LoanTypeForFields.FIXFLIP_SFR]: FIXFLIP_PROPERTY_ECON_FIELDS,
        [LoanTypeForFields.FIXFLIP_UNIT_24]: FIXFLIP_PROPERTY_ECON_FIELDS,
        [LoanTypeForFields.FIXFLIP_CONDO]: FIXFLIP_PROPERTY_ECON_FIELDS,
        [LoanTypeForFields.FIXFLIP_TOWNHOME]: FIXFLIP_PROPERTY_ECON_FIELDS,
        [LoanTypeForFields.GRUP_SFR]: GRUP_PROPERTY_ECON_FIELDS,
        [LoanTypeForFields.GRUP_UNIT_24]: GRUP_PROPERTY_ECON_FIELDS,
        [LoanTypeForFields.GRUP_CONDO]: GRUP_PROPERTY_ECON_FIELDS,
        [LoanTypeForFields.GRUP_TOWNHOME]: GRUP_PROPERTY_ECON_FIELDS,
        [LoanTypeForFields.GRUP_MULTIFAMILYMIXEDUSE]: GRUP_PROPERTY_ECON_FIELDS,
      },
      loanFeatures: {
        quickLoanSizing: [],
        [LoanTypeForFields.FIXFLIP_MULTIFAMILYMIXEDUSE]: FIXFLIP_LOAN_FEATURES,
        [LoanTypeForFields.FIXFLIP_SFR]: FIXFLIP_LOAN_FEATURES,
        [LoanTypeForFields.FIXFLIP_UNIT_24]: FIXFLIP_LOAN_FEATURES,
        [LoanTypeForFields.FIXFLIP_CONDO]: FIXFLIP_LOAN_FEATURES,
        [LoanTypeForFields.FIXFLIP_TOWNHOME]: FIXFLIP_LOAN_FEATURES,
        [LoanTypeForFields.GRUP_SFR]: GRUP_LOAN_FEATURES,
        [LoanTypeForFields.GRUP_UNIT_24]: GRUP_LOAN_FEATURES,
        [LoanTypeForFields.GRUP_CONDO]: GRUP_LOAN_FEATURES,
        [LoanTypeForFields.GRUP_TOWNHOME]: GRUP_LOAN_FEATURES,
        [LoanTypeForFields.GRUP_MULTIFAMILYMIXEDUSE]: GRUP_LOAN_FEATURES,
      },
      loanCharacterisation: {
        quickLoanSizing: [],
        [LoanTypeForFields.FIXFLIP_MULTIFAMILYMIXEDUSE]: FIXFLIP_LOAN_CHAR_FIELDS,
        [LoanTypeForFields.FIXFLIP_SFR]: FIXFLIP_LOAN_CHAR_FIELDS,
        [LoanTypeForFields.FIXFLIP_UNIT_24]: FIXFLIP_LOAN_CHAR_FIELDS,
        [LoanTypeForFields.FIXFLIP_CONDO]: FIXFLIP_LOAN_CHAR_FIELDS,
        [LoanTypeForFields.FIXFLIP_TOWNHOME]: FIXFLIP_LOAN_CHAR_FIELDS,
        [LoanTypeForFields.GRUP_SFR]: GRUP_LOAN_CHAR_FIELDS,
        [LoanTypeForFields.GRUP_UNIT_24]: GRUP_LOAN_CHAR_FIELDS,
        [LoanTypeForFields.GRUP_CONDO]: GRUP_LOAN_CHAR_FIELDS,
        [LoanTypeForFields.GRUP_TOWNHOME]: GRUP_LOAN_CHAR_FIELDS,
        [LoanTypeForFields.GRUP_MULTIFAMILYMIXEDUSE]: GRUP_LOAN_CHAR_FIELDS,
      },
      propLoanFeatures: {
        quickLoanSizing: [],
        [LoanTypeForFields.FIXFLIP_MULTIFAMILYMIXEDUSE]: FIXFLIP_PROP_LOAN_FEATURES,
        [LoanTypeForFields.FIXFLIP_SFR]: FIXFLIP_PROP_LOAN_FEATURES,
        [LoanTypeForFields.FIXFLIP_UNIT_24]: FIXFLIP_PROP_LOAN_FEATURES,
        [LoanTypeForFields.FIXFLIP_CONDO]: FIXFLIP_PROP_LOAN_FEATURES,
        [LoanTypeForFields.FIXFLIP_TOWNHOME]: FIXFLIP_PROP_LOAN_FEATURES,
        [LoanTypeForFields.GRUP_SFR]: GRUP_PROP_LOAN_FEATURES,
        [LoanTypeForFields.GRUP_UNIT_24]: GRUP_PROP_LOAN_FEATURES,
        [LoanTypeForFields.GRUP_CONDO]: ["OriginalAsIsLtv", ...GRUP_PROP_LOAN_FEATURES],
        [LoanTypeForFields.GRUP_TOWNHOME]: GRUP_PROP_LOAN_FEATURES,
        [LoanTypeForFields.GRUP_MULTIFAMILYMIXEDUSE]: GRUP_PROP_LOAN_FEATURES,
      },
      marketData: {
        quickLoanSizing: [],
        [LoanTypeForFields.FIXFLIP_MULTIFAMILYMIXEDUSE]: MF_MARKET_DATA_FIELDS,
        [LoanTypeForFields.GRUP_MULTIFAMILYMIXEDUSE]: MF_MARKET_DATA_FIELDS,
        [LoanTypeForFields.SFR]: [],
        [LoanTypeForFields.UNIT_24]: [],
        [LoanTypeForFields.CONDO]: [],
        [LoanTypeForFields.TOWNHOME]: [],
      },
      subMarketData: {
        quickLoanSizing: [],
        [LoanTypeForFields.FIXFLIP_MULTIFAMILYMIXEDUSE]: MF_SUBMARKET_DATA_FIELDS,
        [LoanTypeForFields.GRUP_MULTIFAMILYMIXEDUSE]: MF_SUBMARKET_DATA_FIELDS,
        [LoanTypeForFields.SFR]: [],
        [LoanTypeForFields.UNIT_24]: [],
        [LoanTypeForFields.CONDO]: [],
        [LoanTypeForFields.TOWNHOME]: [],
      },
    },
    outputFields: {},
  },
};

export default bridgeV2;
